import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Btn, Input, Select, Textarea, Card, Spinner, useToast } from '../components/shared/UI';
import { COLOR_PRESETS, CURRENCIES, PDF_FONTS, PDF_TEMPLATES, THEME_DEFAULTS } from '../utils/helpers';
import { useT } from '../i18n';
import api from '../utils/api';

// Defined outside to prevent remount on every keystroke
function Section({ title, children }) {
  return (
    <Card style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--heading)', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border-light)' }}>{title}</h2>
      {children}
    </Card>
  );
}

// Native colour picker paired with a hex box, so a brand colour can be pasted in
// rather than hunted for on the swatch. The server re-validates the hex and falls
// back to the default, so a half-typed value here can never reach a PDF.
function ColorField({ label, value, onChange }) {
  const { t } = useT();
  const valid = /^#[0-9a-fA-F]{6}$/.test(value || '');
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="color" value={valid ? value : '#000000'}
          onChange={e => onChange(e.target.value.toUpperCase())}
          style={{ width: 42, height: 34, padding: 2, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--input-bg)', cursor: 'pointer', flexShrink: 0 }} />
        <input value={value || ''} onChange={e => onChange(e.target.value)}
          placeholder="#1A3C6E" spellCheck={false}
          style={{ flex: 1, minWidth: 0, padding: '8px 10px', border: '1px solid', borderColor: valid ? 'var(--border)' : 'var(--danger)', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', background: 'var(--input-bg)', color: 'var(--text)', outline: 'none' }} />
      </div>
      {!valid && (
        <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
          {t('settings.hexInvalid')}
        </div>
      )}
    </div>
  );
}

// Miniature mock of each PDF template, drawn with the currently chosen colours
// so the gallery previews the user's own brand rather than a stock blue.
function TplThumb({ tpl, header, accent }) {
  const W = o => `rgba(255,255,255,${o})`;
  const ln = (w, c, h = 5, s = {}) => <div style={{ width: w, height: h, background: c, borderRadius: 2, ...s }} />;
  // Two-column band: coloured backgrounds with a name line + sub line left, doc-type line right.
  const split = (lBg, rBg, lLine, subLine, rLine, outer = {}) => (
    <div style={{ display: 'flex', height: 26, ...outer }}>
      <div style={{ flex: 1.6, background: lBg, padding: '6px 7px' }}>
        {ln('65%', lLine)}
        {ln('45%', subLine, 3, { marginTop: 3 })}
      </div>
      <div style={{ flex: 1, background: rBg, padding: '6px 7px', display: 'flex', justifyContent: 'flex-end' }}>
        {ln('70%', rLine)}
      </div>
    </div>
  );
  // White band with name left, doc-type right, plus outer chrome (rules, frames).
  const flat = (lLine, rLine, outer = {}) => (
    <div style={{ height: 26, padding: '7px 7px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', ...outer }}>
      {ln('45%', lLine)}
      {ln('22%', rLine)}
    </div>
  );
  let head;
  switch (tpl) {
    case 'minimal': head = flat(header, accent, { borderBottom: `2px solid ${accent}` }); break;
    case 'bold': head = (
      <div style={{ height: 26, background: header, padding: '0 7px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {ln('40%', W(0.9))}
        {ln('26%', accent, 10, { borderRadius: 3 })}
      </div>
    ); break;
    case 'elegant': head = (
      <div style={{ height: 26, borderTop: `3px double ${header}`, borderBottom: `3px double ${header}`, padding: '5px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        {ln('46%', header, 4)}
        {ln('30%', accent, 4)}
      </div>
    ); break;
    case 'gradient': head = split('transparent', 'transparent', W(0.9), W(0.5), W(0.9),
      { background: `linear-gradient(100deg, ${header}, ${accent})` }); break;
    case 'slate': head = split('#263238', '#263238', W(0.9), W(0.5), accent,
      { borderBottom: `2px solid ${accent}` }); break;
    case 'soft': head = split(`${header}18`, `${accent}20`, header, '#B9C2D0', accent); break;
    case 'stripe': head = flat(header, header, { borderTop: `4px solid ${accent}`, borderBottom: '1px solid #E1E6EE' }); break;
    case 'frame': head = flat(header, accent, { border: `2px solid ${header}` }); break;
    case 'mono': head = flat('#111', '#111', { borderBottom: '2px solid #111' }); break;
    case 'sidebar': head = flat(header, header, { background: '#F7F9FC', borderLeft: `5px solid ${accent}` }); break;
    case 'ledger': head = flat('#333', header, { borderBottom: '3px double #333' }); break;
    case 'slim': head = (
      <div style={{ height: 14, marginTop: 6, background: header, borderRadius: 3, padding: '0 7px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {ln('35%', W(0.9), 4)}
        {ln('25%', W(0.9), 4)}
      </div>
    ); break;
    case 'curve': head = split(header, header, W(0.9), W(0.5), W(0.9),
      { borderRadius: '0 0 10px 10px', borderBottom: `3px solid ${accent}`, overflow: 'hidden' }); break;
    case 'diagonal': head = split('transparent', 'transparent', W(0.9), W(0.5), W(0.9),
      { background: `linear-gradient(105deg, ${header} 55%, ${accent} 55%)` }); break;
    case 'banner': head = (
      <div style={{ height: 26 }}>
        <div style={{ height: 16, background: header, padding: '4px 7px' }}>{ln('50%', W(0.9), 4)}</div>
        <div style={{ height: 10, background: accent, padding: '3px 7px', display: 'flex', justifyContent: 'space-between' }}>
          {ln('25%', W(0.9), 4)}
          {ln('20%', W(0.7), 4)}
        </div>
      </div>
    ); break;
    case 'duo': head = split(accent, header, W(0.9), W(0.5), W(0.9)); break;
    case 'cards': head = (
      <div style={{ display: 'flex', gap: 4, height: 26, padding: '2px 4px 0' }}>
        <div style={{ flex: 1.6, border: `1.5px solid ${header}`, borderRadius: 5, padding: '4px 5px' }}>{ln('60%', header, 4)}</div>
        <div style={{ flex: 1, border: `1.5px solid ${accent}`, borderRadius: 5, padding: '4px 5px', display: 'flex', justifyContent: 'flex-end' }}>{ln('60%', accent, 4)}</div>
      </div>
    ); break;
    case 'pop': head = flat('#111', accent,
      { border: '2px solid #111', boxShadow: `3px 3px 0 ${accent}`, margin: '0 4px 4px 2px', height: 22 }); break;
    case 'royal': head = split(header, header, W(0.9), W(0.5), W(0.9),
      { border: `2px double ${accent}`, padding: 2 }); break;
    case 'marker': head = (
      <div style={{ height: 26, borderBottom: '2px solid #111', padding: '5px 7px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {ln('45%', accent, 8)}
        {ln('22%', '#111', 8)}
      </div>
    ); break;
    case 'sketch': head = flat(header, accent, { border: `1.5px dashed ${header}`, borderRadius: 6 }); break;
    case 'ghost': head = (
      <div style={{ display: 'flex', height: 26 }}>
        <div style={{ flex: 1.6, background: header, padding: '6px 7px' }}>
          {ln('65%', W(0.9))}
          {ln('45%', W(0.5), 3, { marginTop: 3 })}
        </div>
        <div style={{ flex: 1, background: header, padding: '6px 7px', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: '70%', height: 6, border: '1.5px solid rgba(255,255,255,0.9)', borderRadius: 2, boxSizing: 'border-box' }} />
        </div>
      </div>
    ); break;
    case 'midnight': head = split('#0B1220', '#0B1220', W(0.9), W(0.5), accent,
      { borderBottom: '2px solid', borderImage: `linear-gradient(90deg, ${header}, ${accent}) 1` }); break;
    default: head = split(header, accent, W(0.9), W(0.5), W(0.9)); // classic
  }
  // Table-head / grand-total bar treatment per template.
  const BARS = {
    classic: { bg: header }, bold: { bg: accent }, stripe: { bg: header },
    sidebar: { bg: header }, slate: { bg: '#263238' }, slim: { bg: header },
    curve: { bg: header }, gradient: { bg: `linear-gradient(90deg, ${header}, ${accent})` },
    soft: { bg: `${header}26` },
    minimal: { rule: header }, elegant: { rule: header }, mono: { rule: '#111' },
    frame: { bg: '#fff', box: header }, ledger: { bg: '#F2F4F8', box: '#999' },
    diagonal: { bg: header }, banner: { bg: header }, duo: { bg: accent },
    cards: { bg: header }, pop: { bg: '#111' }, royal: { bg: header },
    marker: { bg: '#111' }, sketch: { dash: header }, ghost: { bg: header },
    midnight: { bg: '#0B1220' },
  };
  const bar = pos => {
    const b = BARS[tpl] || BARS.classic;
    if (b.rule || b.dash) return <div style={{ height: 6, [pos === 'top' ? 'borderBottom' : 'borderTop']: `1.5px ${b.dash ? 'dashed' : 'solid'} ${b.rule || b.dash}` }} />;
    return <div style={{ height: 6, background: b.bg, borderRadius: 2, ...(b.box ? { border: `1px solid ${b.box}` } : {}) }} />;
  };
  return (
    <div style={{ background: '#fff' }}>
      {head}
      <div style={{ padding: '7px 7px 9px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {bar('top')}
        <div style={{ height: 3, background: '#E3E8F0', borderRadius: 2, width: '92%' }} />
        <div style={{ height: 3, background: '#E3E8F0', borderRadius: 2, width: '82%' }} />
        {bar('bottom')}
      </div>
    </div>
  );
}

const TPL_LABEL_KEYS = {
  classic: 'settings.tplClassic',
  minimal: 'settings.tplMinimal',
  bold: 'settings.tplBold',
  elegant: 'settings.tplElegant',
  gradient: 'settings.tplGradient',
  slate: 'settings.tplSlate',
  soft: 'settings.tplSoft',
  stripe: 'settings.tplStripe',
  frame: 'settings.tplFrame',
  mono: 'settings.tplMono',
  sidebar: 'settings.tplSidebar',
  ledger: 'settings.tplLedger',
  slim: 'settings.tplSlim',
  curve: 'settings.tplCurve',
  diagonal: 'settings.tplDiagonal',
  banner: 'settings.tplBanner',
  duo: 'settings.tplDuo',
  cards: 'settings.tplCards',
  pop: 'settings.tplPop',
  royal: 'settings.tplRoyal',
  marker: 'settings.tplMarker',
  sketch: 'settings.tplSketch',
  ghost: 'settings.tplGhost',
  midnight: 'settings.tplMidnight',
};

const PRESET_LABEL_KEYS = {
  navy: 'settings.presetNavy',
  sky: 'settings.presetSky',
  teal: 'settings.presetTeal',
  emerald: 'settings.presetEmerald',
  burgundy: 'settings.presetBurgundy',
  crimson: 'settings.presetCrimson',
  copper: 'settings.presetCopper',
  plum: 'settings.presetPlum',
  rose: 'settings.presetRose',
  graphite: 'settings.presetGraphite',
};

// Per-template chrome for the live preview, mirroring the override CSS in
// server/utils/theme.js templateCss(). `base` is the classic look.
function previewSpec(tpl, header, accent) {
  const base = {
    outer: {}, leftBg: header, rightBg: accent, leftExtra: {}, rightExtra: {},
    nameColor: '#fff', nameExtra: {}, contactColor: 'rgba(255,255,255,0.9)',
    docType: { color: '#fff' }, metaColor: 'rgba(255,255,255,0.95)',
    thead: { background: header, color: '#fff' },
    total: { background: header, color: '#fff' },
    secHdr: {}, zebra: true, slim: false, stacked: false,
  };
  // Shared "white header, dark text" starting point for the inky templates.
  const ink = { leftBg: '#fff', rightBg: '#fff', contactColor: '#555', metaColor: '#444', zebra: false };
  switch (tpl) {
    case 'minimal': return { ...base, ...ink, outer: { borderBottom: `3px solid ${accent}` }, nameColor: header,
      docType: { color: accent }, thead: { color: header, borderBottom: `2px solid ${header}` },
      total: { color: header, borderTop: `2px solid ${header}` } };
    case 'bold': return { ...base, rightBg: header,
      docType: { color: '#fff', background: accent, display: 'inline-block', padding: '2px 10px', borderRadius: 5 },
      thead: { background: accent, color: '#fff' }, total: { background: accent, color: '#fff' },
      secHdr: { background: header, color: '#fff', borderBottom: 'none', padding: '6px 10px', borderRadius: 4 } };
    case 'elegant': return { ...base, ...ink, nameColor: header, docType: { color: accent, letterSpacing: 5 },
      thead: { color: header, borderTop: `1.5px solid ${header}`, borderBottom: `1.5px solid ${header}` },
      total: { color: header, borderTop: `3px double ${header}`, borderBottom: `3px double ${header}` } };
    case 'gradient': return { ...base, outer: { background: `linear-gradient(100deg, ${header}, ${accent})` },
      leftBg: 'transparent', rightBg: 'transparent',
      thead: { background: `linear-gradient(90deg, ${header}, ${accent})`, color: '#fff' },
      total: { background: `linear-gradient(90deg, ${header}, ${accent})`, color: '#fff' } };
    case 'slate': return { ...base, outer: { borderBottom: `3px solid ${accent}` }, leftBg: '#263238', rightBg: '#263238',
      docType: { color: accent }, thead: { background: '#263238', color: '#fff' },
      total: { background: '#263238', color: '#fff' }, secHdr: { color: '#263238' } };
    case 'soft': return { ...base, leftBg: `${header}18`, rightBg: `${accent}20`, nameColor: header,
      contactColor: '#555', docType: { color: accent }, metaColor: '#444',
      thead: { background: `${header}18`, color: header }, zebra: false };
    case 'stripe': return { ...base, ...ink, zebra: true,
      outer: { borderTop: `5px solid ${accent}`, borderBottom: '1px solid #E1E6EE' },
      nameColor: header, docType: { color: header } };
    case 'frame': return { ...base, ...ink, outer: { border: `2px solid ${header}` },
      rightExtra: { borderLeft: `2px solid ${header}` }, nameColor: header, docType: { color: accent },
      thead: { color: header, border: `1.5px solid ${header}` }, total: { color: header, border: `1.5px solid ${header}` } };
    case 'mono': return { ...base, ...ink, outer: { borderBottom: '3px solid #111' }, nameColor: '#111',
      contactColor: '#444', docType: { color: '#111' }, metaColor: '#333',
      thead: { color: '#111', borderBottom: '2px solid #111' },
      total: { color: '#111', borderTop: '3px double #111' },
      secHdr: { color: '#111', borderBottom: '1.5px solid #111' } };
    case 'sidebar': return { ...base, outer: { background: '#F7F9FC', borderLeft: `6px solid ${accent}` },
      leftBg: 'transparent', rightBg: 'transparent', nameColor: header, contactColor: '#555',
      docType: { color: header }, metaColor: '#444',
      secHdr: { borderBottom: 'none', borderLeft: `5px solid ${accent}`, paddingLeft: 8 } };
    case 'ledger': return { ...base, ...ink, outer: { borderBottom: '6px double #333' }, nameColor: '#1a1a2e',
      docType: { color: header },
      thead: { background: '#F2F4F8', color: '#1a1a2e', border: '1px solid #999' },
      total: { background: '#F2F4F8', color: '#1a1a2e', border: '1px solid #999', borderTop: '2px solid #333' },
      secHdr: { color: '#1a1a2e', borderBottom: '1px solid #333' } };
    case 'slim': return { ...base, slim: true };
    case 'curve': return { ...base, rightBg: header,
      outer: { borderRadius: '0 0 22px 22px', borderBottom: `4px solid ${accent}`, overflow: 'hidden' } };
    case 'diagonal': return { ...base, leftBg: 'transparent', rightBg: 'transparent',
      outer: { background: `linear-gradient(105deg, ${header} 55%, ${accent} 55%)` } };
    case 'banner': return { ...base, stacked: true };
    case 'duo': return { ...base, leftBg: accent, rightBg: header,
      thead: { background: accent, color: '#fff' }, total: { background: accent, color: '#fff' },
      secHdr: { color: header, borderBottom: `1.5px solid ${header}` } };
    case 'cards': return { ...base, ...ink, zebra: true, outer: { gap: 10 },
      leftExtra: { border: `2px solid ${header}`, borderRadius: 12 },
      rightExtra: { border: `2px solid ${accent}`, borderRadius: 12 },
      nameColor: header, docType: { color: accent } };
    case 'pop': return { ...base, ...ink, zebra: true, nameColor: '#111',
      outer: { border: '3px solid #111', boxShadow: `5px 5px 0 ${accent}`, margin: '0 5px 5px 0' },
      rightExtra: { borderLeft: '3px solid #111' }, contactColor: '#444',
      docType: { color: accent }, metaColor: '#333',
      thead: { background: '#111', color: '#fff' }, total: { background: '#111', color: '#fff' },
      secHdr: { color: '#111', borderBottom: '3px solid #111' } };
    case 'royal': return { ...base, rightBg: header,
      outer: { border: `5px double ${accent}`, padding: 5, background: '#fff' },
      docType: { color: '#fff', letterSpacing: 3 },
      secHdr: { color: header, borderBottom: `3px double ${accent}` } };
    case 'marker': return { ...base, ...ink, outer: { borderBottom: '2px solid #111' },
      nameColor: '#fff', nameExtra: { background: accent, display: 'inline-block', padding: '1px 8px' },
      docType: { color: '#fff', background: '#111', display: 'inline-block', padding: '2px 10px' },
      thead: { background: '#111', color: '#fff' }, total: { background: accent, color: '#fff' },
      secHdr: { background: `${accent}30`, color: '#111', borderBottom: 'none', display: 'table', padding: '3px 10px' } };
    case 'sketch': return { ...base, ...ink, outer: { border: `2px dashed ${header}`, borderRadius: 10 },
      rightExtra: { borderLeft: `2px dashed ${header}` }, nameColor: header, docType: { color: accent },
      thead: { color: header, borderBottom: `2px dashed ${header}` },
      total: { color: header, borderTop: `2px dashed ${header}` } };
    case 'ghost': return { ...base, rightBg: header,
      docType: { color: 'transparent', WebkitTextStroke: '1.5px #fff' } };
    case 'midnight': return { ...base, leftBg: '#0B1220', rightBg: '#0B1220',
      outer: { borderBottom: '3px solid', borderImage: `linear-gradient(90deg, ${header}, ${accent}) 1` },
      docType: { color: accent }, thead: { background: '#0B1220', color: '#fff' },
      total: { background: `linear-gradient(90deg, ${header}, ${accent})`, color: '#fff' } };
    default: return base; // classic
  }
}

function ThemePreview({ form }) {
  const header = /^#[0-9a-fA-F]{6}$/.test(form.themeHeaderColor) ? form.themeHeaderColor : THEME_DEFAULTS.themeHeaderColor;
  const accent = /^#[0-9a-fA-F]{6}$/.test(form.themeAccentColor) ? form.themeAccentColor : THEME_DEFAULTS.themeAccentColor;
  const size   = Number(form.themeFontSize) >= 9 && Number(form.themeFontSize) <= 18 ? Number(form.themeFontSize) : 13;
  const scale  = size / 13;
  const px     = n => `${(n * scale).toFixed(1)}px`;
  const hs     = Number(form.themeHeaderSize) >= 50 && Number(form.themeHeaderSize) <= 150 ? Number(form.themeHeaderSize) / 100 : 1;
  const hpx    = n => `${(n * scale * hs).toFixed(1)}px`;
  // Per-textbox multipliers — same validation and authored bases as the server.
  const part     = (v, base) => (Number(v) >= 6 && Number(v) <= 60 ? Number(v) / base : 1);
  const pName    = part(form.themeHeaderNameSize, 26);
  const pContact = part(form.themeHeaderContactSize, 11.5);
  const pDocType = part(form.themeHeaderDocTypeSize, 28);
  const pMeta    = part(form.themeHeaderMetaSize, 12);
  const family = `'${form.themeFontFamily || 'Segoe UI'}', Arial, sans-serif`;
  const tpl = PDF_TEMPLATES.includes(form.themeTemplate) ? form.themeTemplate : 'classic';
  const s = previewSpec(tpl, header, accent);

  const pad = s.slim ? `${hpx(8)} ${hpx(14)}` : `${hpx(14)} ${hpx(16)}`;
  const nameEl = <div style={{ fontSize: hpx((s.slim ? 15 : 20) * pName), fontWeight: 800, lineHeight: 1.2, color: s.nameColor, ...s.nameExtra }}>{form.name || 'YOUR COMPANY'}</div>;
  const contactEl = <div style={{ fontSize: hpx(10.5 * pContact), color: s.contactColor }}>{form.email || 'hello@example.com'}</div>;

  let headerBand;
  if (tpl === 'elegant') headerBand = (
    <div style={{ textAlign: 'center', padding: `${hpx(14)} ${hpx(16)}`, borderTop: `4px double ${header}`, borderBottom: `4px double ${header}` }}>
      {form.logoUrl && <img src={form.logoUrl} alt="" style={{ height: 30, maxWidth: 70, objectFit: 'contain', marginBottom: 4 }} />}
      {nameEl}
      <div style={{ marginTop: 4 }}>{contactEl}</div>
      <div style={{ fontSize: hpx(18 * pDocType), fontWeight: 900, letterSpacing: 5, color: accent, marginTop: 8 }}>INVOICE</div>
      <div style={{ fontSize: hpx(10.5 * pMeta), color: '#444', marginTop: 2 }}>INV-2026-0001</div>
    </div>
  );
  else if (s.stacked) headerBand = (
    <div style={s.outer}>
      <div style={{ background: s.leftBg, padding: `${hpx(12)} ${hpx(16)}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          {form.logoUrl && <img src={form.logoUrl} alt="" style={{ height: 26, maxWidth: 70, objectFit: 'contain', flexShrink: 0 }} />}
          {nameEl}
        </div>
        {contactEl}
      </div>
      <div style={{ background: s.rightBg, padding: `${hpx(7)} ${hpx(16)}`, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ fontSize: hpx(14 * pDocType), fontWeight: 900, letterSpacing: 1.5, ...s.docType }}>INVOICE</div>
        <div style={{ fontSize: hpx(10.5 * pMeta), color: s.metaColor }}>INV-2026-0001</div>
      </div>
    </div>
  );
  else headerBand = (
    <div style={{ display: 'flex', ...s.outer }}>
      <div style={{ background: s.leftBg, padding: pad, flex: 1.5, ...s.leftExtra }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: form.themeHeaderCompact || s.slim ? 0 : 6 }}>
          {form.logoUrl && <img src={form.logoUrl} alt="" style={{ height: s.slim ? 22 : 30, maxWidth: 70, objectFit: 'contain', flexShrink: 0 }} />}
          <div>
            {nameEl}
            {form.themeHeaderCompact && <div style={{ marginTop: 3 }}>{contactEl}</div>}
          </div>
        </div>
        {!form.themeHeaderCompact && !s.slim && contactEl}
      </div>
      <div style={{ background: s.rightBg, padding: pad, textAlign: 'right', minWidth: 130 * hs, ...s.rightExtra }}>
        <div style={{ fontSize: hpx((s.slim ? 15 : 18) * pDocType), fontWeight: 900, letterSpacing: 1.5, ...s.docType }}>INVOICE</div>
        <div style={{ fontSize: hpx(10.5 * pMeta), color: s.metaColor, marginTop: 2 }}>INV-2026-0001</div>
      </div>
    </div>
  );

  const theadStyle = s.thead;
  const totalStyle = s.total;
  const secHdrStyle = { fontSize: px(13), fontWeight: 700, color: accent, borderBottom: `1.5px solid ${accent}`, paddingBottom: 5, marginBottom: 10, ...s.secHdr };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', fontFamily: family, background: '#fff', color: '#1a1a2e' }}>
      {headerBand}
      {/* Section heading + table head */}
      <div style={{ padding: '14px 16px' }}>
        <div style={secHdrStyle}>
          1. &nbsp; Items
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: px(11.5) }}>
          <thead>
            <tr>
              <th style={{ padding: '7px 9px', textAlign: 'left', fontWeight: 600, ...theadStyle }}>Description</th>
              <th style={{ padding: '7px 9px', textAlign: 'right', fontWeight: 600, width: 90, ...theadStyle }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={{ padding: '7px 9px', borderBottom: '1px solid #E8ECF2' }}>Consulting services</td>
                <td style={{ padding: '7px 9px', borderBottom: '1px solid #E8ECF2', textAlign: 'right' }}>RM 1,200.00</td></tr>
            <tr style={{ background: s.zebra ? '#F7F9FC' : '#fff' }}>
                <td style={{ padding: '7px 9px', borderBottom: '1px solid #E8ECF2' }}>Site visit</td>
                <td style={{ padding: '7px 9px', borderBottom: '1px solid #E8ECF2', textAlign: 'right' }}>RM 300.00</td></tr>
            <tr>
                <td style={{ padding: '8px 9px', textAlign: 'right', fontWeight: 700, ...totalStyle }}>TOTAL INVOICE</td>
                <td style={{ padding: '8px 9px', textAlign: 'right', fontWeight: 700, ...totalStyle }}>RM 1,500.00</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Settings() {
  const [form, setForm] = useState({
    name: '', address: '', city: '', phone: '', email: '', website: '',
    regNo: '', taxNo: '', currency: 'MYR', currencySymbol: 'RM',
    bankName: '', bankAccount: '', bankHolder: '', terms: '',
    logoUrl: '', logoHeight: 60,
    signatoryName: '', signatoryTitle: '', signatureUrl: '',
    pdfMargin: 2.54,
    ...THEME_DEFAULTS
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const { show, ToastContainer } = useToast();
  const { t } = useT();
  const navigate = useNavigate();
  const [einv, setEinv] = useState(null);

  useEffect(() => { api.get('/einvoice/settings').then(r => setEinv(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    api.get('/company').then(r => {
      if (r.data) setForm(f => ({ ...f, ...r.data }));
    }).finally(() => setLoading(false));
  }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleLogo = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setForm(f => ({ ...f, logoUrl: reader.result }));
    reader.readAsDataURL(file);
  };

  // Rewrites the logo as a PNG with near-white pixels turned transparent, so a
  // baked-in white box disappears on the coloured header band. Applies to the
  // stored image itself — PDF, Word and previews all pick it up. Re-upload to undo.
  const removeLogoBackground = () => {
    if (!form.logoUrl) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const px = data.data;
      for (let i = 0; i < px.length; i += 4) {
        if (px[i] > 235 && px[i + 1] > 235 && px[i + 2] > 235) px[i + 3] = 0;
      }
      ctx.putImageData(data, 0, 0);
      setForm(f => ({ ...f, logoUrl: canvas.toDataURL('image/png') }));
      show(t('settings.whiteBgRemoved'));
    };
    img.onerror = () => show(t('settings.logoProcessFailed'), 'error');
    img.src = form.logoUrl;
  };

  const handleSignature = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setForm(f => ({ ...f, signatureUrl: reader.result }));
    reader.readAsDataURL(file);
  };

  const setCurrency = e => {
    const cur = CURRENCIES.find(c => c.code === e.target.value) || CURRENCIES[0];
    setForm(f => ({ ...f, currency: cur.code, currencySymbol: cur.symbol }));
  };

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      // Take the server's copy back: it normalises some fields (e.g. reminder days "1, 7,14" → [1, 7, 14])
      const { data } = await api.put('/company', form);
      if (data) setForm(f => ({ ...f, ...data }));
      show(t('settings.saved'));
    } catch { show(t('settings.errorSaving'), 'error'); }
    finally { setSaving(false); }
  };

  if (loading) return <Spinner />;

  return (
    <form onSubmit={save}>
      <ToastContainer />
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')}
        actions={<Btn type="submit" variant="primary" disabled={saving}>{saving ? t('common.saving') : t('settings.saveSettings')}</Btn>} />

      <div style={{ maxWidth: 760 }}>

        <Section title={t('settings.companyLogo')}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
            {/* Preview */}
            <div style={{ flexShrink: 0, width: 160, height: Number(form.logoHeight) + 20, border: '1px dashed var(--border)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: /^#[0-9a-fA-F]{6}$/.test(form.themeHeaderColor) ? form.themeHeaderColor : THEME_DEFAULTS.themeHeaderColor, overflow: 'hidden' }}>
              {form.logoUrl
                ? <img src={form.logoUrl} alt="Logo" style={{ maxWidth: '100%', height: Number(form.logoHeight), objectFit: 'contain' }} />
                : <span style={{ fontSize: 12, color: 'var(--text-subtle)' }}>{t('settings.preview')}</span>}
            </div>
            {/* Controls */}
            <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>{t('settings.uploadImage')}</div>
                <input type="file" accept="image/*" onChange={handleLogo} style={{ fontSize: 13, color: 'var(--text)' }} />
                {form.logoUrl && (
                  <>
                    <button type="button" onClick={removeLogoBackground}
                      style={{ display: 'inline-block', marginLeft: 12, fontSize: 12, color: 'var(--primary, #2E75B6)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                      {t('settings.removeWhiteBg')}
                    </button>
                    <button type="button" onClick={() => setForm(f => ({ ...f, logoUrl: '' }))}
                      style={{ display: 'inline-block', marginLeft: 12, fontSize: 12, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      {t('settings.remove')}
                    </button>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Input label={t('settings.logoHeight')} type="number" min="20" max="200" value={form.logoHeight}
                  onChange={set('logoHeight')} style={{ width: 140 }} />
                <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 18 }}>{t('settings.previewNote')}</p>
              </div>
            </div>
          </div>
        </Section>

        <Section title={t('settings.companyInfo')}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14 }}>
            <Input label={t('settings.companyName')} value={form.name} onChange={set('name')} placeholder="Alaganu Enterprise" style={{ gridColumn: '1 / -1' }} />
            <Input label={t('settings.address')} value={form.address} onChange={set('address')} placeholder="No 5, Jalan Puchong" />
            <Input label={t('settings.cityState')} value={form.city} onChange={set('city')} placeholder="Puchong, Selangor" />
            <Input label={t('settings.phone')} value={form.phone} onChange={set('phone')} placeholder="010-665-0171" />
            <Input label={t('settings.email')} type="email" value={form.email} onChange={set('email')} placeholder="new.technology@hotmail.com" />
            <Input label={t('settings.website')} value={form.website} onChange={set('website')} placeholder="www.alaganu.com.my" />
            <Input label={t('settings.regNo')} value={form.regNo} onChange={set('regNo')} placeholder="SA0012345-X" />
            <Input label={t('settings.taxNo')} value={form.taxNo} onChange={set('taxNo')} placeholder="B16-1234-56789012-0" />
          </div>
        </Section>

        <Section title={t('settings.docAppearance')}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
            {t('settings.docAppearanceIntro')}
          </p>

          {/* Template gallery */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
              {t('settings.templateGallery')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
              {PDF_TEMPLATES.map(tpl => {
                const selected = (form.themeTemplate || 'classic') === tpl;
                return (
                  <button key={tpl} type="button"
                    onClick={() => setForm(f => ({ ...f, themeTemplate: tpl }))}
                    style={{ padding: 0, border: '2px solid', borderColor: selected ? 'var(--primary, #2E75B6)' : 'var(--border)', borderRadius: 10, background: '#fff', cursor: 'pointer', overflow: 'hidden', boxShadow: selected ? '0 0 0 3px rgba(46,117,182,0.15)' : 'none' }}>
                    <TplThumb tpl={tpl}
                      header={/^#[0-9a-fA-F]{6}$/.test(form.themeHeaderColor) ? form.themeHeaderColor : THEME_DEFAULTS.themeHeaderColor}
                      accent={/^#[0-9a-fA-F]{6}$/.test(form.themeAccentColor) ? form.themeAccentColor : THEME_DEFAULTS.themeAccentColor} />
                    <div style={{ padding: '6px 8px', fontSize: 12, fontWeight: 600, textAlign: 'center', color: selected ? 'var(--primary, #2E75B6)' : 'var(--text-muted)', borderTop: '1px solid var(--border-light)' }}>
                      {t(TPL_LABEL_KEYS[tpl])}
                    </div>
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 6 }}>
              {t('settings.templateGalleryHint')}
            </p>
          </div>

          {/* Colour presets — one click sets both colours; composes with any template */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
              {t('settings.colorPresets')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))', gap: 10 }}>
              {COLOR_PRESETS.map(p => {
                const selected = form.themeHeaderColor === p.header && form.themeAccentColor === p.accent;
                return (
                  <button key={p.id} type="button"
                    onClick={() => setForm(f => ({ ...f, themeHeaderColor: p.header, themeAccentColor: p.accent }))}
                    style={{ padding: 0, border: '2px solid', borderColor: selected ? 'var(--primary, #2E75B6)' : 'var(--border)', borderRadius: 8, background: 'var(--input-bg)', cursor: 'pointer', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', height: 26 }}>
                      <div style={{ flex: 1.4, background: p.header }} />
                      <div style={{ flex: 1, background: p.accent }} />
                    </div>
                    <div style={{ padding: '3px 4px', fontSize: 11, fontWeight: 600, textAlign: 'center', color: selected ? 'var(--primary, #2E75B6)' : 'var(--text-muted)', borderTop: '1px solid var(--border-light)' }}>
                      {t(PRESET_LABEL_KEYS[p.id])}
                    </div>
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 6 }}>
              {t('settings.colorPresetsHint')}
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 14 }}>
            <ColorField label={t('settings.headerColour')} value={form.themeHeaderColor}
              onChange={v => setForm(f => ({ ...f, themeHeaderColor: v }))} />
            <ColorField label={t('settings.accentColour')} value={form.themeAccentColor}
              onChange={v => setForm(f => ({ ...f, themeAccentColor: v }))} />
            <Select label={t('settings.font')} value={form.themeFontFamily} onChange={set('themeFontFamily')}>
              {PDF_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
            </Select>
            <Input label={t('settings.bodySize')} type="number" min="9" max="18" step="0.5"
              value={form.themeFontSize} onChange={set('themeFontSize')} />
            <Input label={t('settings.headerSizePct')} type="number" min="50" max="150" step="5"
              value={form.themeHeaderSize} onChange={set('themeHeaderSize')} />
          </div>

          {/* Per-textbox header font sizes */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
              {t('settings.headerTextSizes')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))', gap: 14 }}>
              <Input label={t('settings.hCompanyName')} type="number" min="6" max="60" step="0.5"
                value={form.themeHeaderNameSize} onChange={set('themeHeaderNameSize')} />
              <Input label={t('settings.hTagline')} type="number" min="6" max="60" step="0.5"
                value={form.themeHeaderTaglineSize} onChange={set('themeHeaderTaglineSize')} />
              <Input label={t('settings.hContact')} type="number" min="6" max="60" step="0.5"
                value={form.themeHeaderContactSize} onChange={set('themeHeaderContactSize')} />
              <Input label={t('settings.hDocTitle')} type="number" min="6" max="60" step="0.5"
                value={form.themeHeaderDocTypeSize} onChange={set('themeHeaderDocTypeSize')} />
              <Input label={t('settings.hMeta')} type="number" min="6" max="60" step="0.5"
                value={form.themeHeaderMetaSize} onChange={set('themeHeaderMetaSize')} />
            </div>
          </div>

          {/* Compact header layout */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>
            <input type="checkbox" checked={!!form.themeHeaderCompact}
              onChange={e => setForm(f => ({ ...f, themeHeaderCompact: e.target.checked }))} />
            <span>
              <strong>{t('settings.compactHeader')}</strong>
              <span style={{ color: 'var(--text-muted)' }}>{t('settings.compactHeaderHint')}</span>
            </span>
          </label>

          {/* Prepared For / Issued By section */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
              {t('settings.partySection')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 14 }}>
              <Input label={t('settings.partyLabels')} type="number" min="6" max="60" step="0.5"
                value={form.themePartyLabelSize} onChange={set('themePartyLabelSize')} />
              <Input label={t('settings.partyNames')} type="number" min="6" max="60" step="0.5"
                value={form.themePartyNameSize} onChange={set('themePartyNameSize')} />
              <Input label={t('settings.partyDetails')} type="number" min="6" max="60" step="0.5"
                value={form.themePartyDetailSize} onChange={set('themePartyDetailSize')} />
              <Input label={t('settings.headerGap')} type="number" min="0" max="120" step="2"
                value={form.themeHeaderGap} onChange={set('themeHeaderGap')} />
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 6 }}>
              {t('settings.headerGapHint')}
            </p>
          </div>

          {/* Live preview — mirrors the real PDF header, section heading and table head */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>{t('settings.preview')}</div>
            <ThemePreview form={form} />
          </div>

          <button type="button"
            onClick={() => setForm(f => ({ ...f, ...THEME_DEFAULTS }))}
            style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
            {t('settings.resetTheme')}
          </button>
        </Section>

        <Section title={t('settings.currencyDefaults')}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14 }}>
            <Select label={t('settings.defaultCurrency')} value={form.currency} onChange={setCurrency}>
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
            </Select>
            <Input label={t('settings.pdfMargin')} type="number" min="0.5" max="5" step="0.1"
              value={form.pdfMargin} onChange={set('pdfMargin')}
              placeholder="2.54" />
          </div>
          <div style={{ marginTop: 14 }}>
            <Textarea label={t('settings.defaultTerms')} value={form.terms} onChange={set('terms')}
              placeholder={t('settings.termsPh')}
              rows={5} />
          </div>
        </Section>

        <Section title={t('settings.bankDetails')}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
            {t('settings.bankDetailsIntro')}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14 }}>
            <Input label={t('settings.bankName')} value={form.bankName} onChange={set('bankName')} placeholder="Maybank / CIMB / Public Bank" />
            <Input label={t('settings.accountNumber')} value={form.bankAccount} onChange={set('bankAccount')} placeholder="1234-5678-9012" />
            <Input label={t('settings.accountHolder')} value={form.bankHolder} onChange={set('bankHolder')} placeholder="Alaganu Enterprise" style={{ gridColumn: '1 / -1' }} />
          </div>
        </Section>

        <Section title={`🏛 ${t('einv.title')}`}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>{t('einv.settingsIntro')}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <Btn type="button" variant="outline" onClick={() => navigate('/settings/einvoice')}>{t('einv.open')} →</Btn>
            <span style={{ fontSize: 13, fontWeight: 600, color: einv?.enabled ? 'var(--success)' : 'var(--text-subtle)' }}>
              {einv?.enabled ? `● ${t('einv.statusOn', { env: t(`einv.${einv.environment}`) })}` : t('einv.statusOff')}
            </span>
          </div>
        </Section>

        <Section title={`⏰ ${t('reminders.title')}`}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>{t('reminders.intro')}</p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text)', cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={!!form.reminderEnabled}
              onChange={e => setForm(f => ({ ...f, reminderEnabled: e.target.checked }))} />
            {t('reminders.enable')}
          </label>
          {form.reminderEnabled && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14 }}>
                <Input label={t('reminders.before')} type="number" min="0" max="30"
                  value={form.reminderBeforeDays ?? 3} onChange={set('reminderBeforeDays')} />
                <div>
                  <Input label={t('reminders.after')} placeholder="1, 7, 14"
                    value={Array.isArray(form.reminderAfterDays) ? form.reminderAfterDays.join(', ') : (form.reminderAfterDays ?? '')}
                    onChange={set('reminderAfterDays')} />
                  <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 4 }}>{t('reminders.afterHelp')}</p>
                </div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 10 }}>{t('reminders.needsEmail')}</p>
            </>
          )}
        </Section>

        <Section title={t('settings.signatory')}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14, marginBottom: 16 }}>
            <Input label={t('settings.signatoryName')} value={form.signatoryName} onChange={set('signatoryName')} placeholder="Mustafa Muwafak Alobaedy" />
            <Input label={t('settings.designation')} value={form.signatoryTitle} onChange={set('signatoryTitle')} placeholder="Managing Director" />
          </div>
          <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10 }}>{t('settings.signatureImage')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ width: 160, height: 70, border: '1px dashed var(--border)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', flexShrink: 0, overflow: 'hidden' }}>
                {form.signatureUrl
                  ? <img src={form.signatureUrl} alt="Signature" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <span style={{ fontSize: 12, color: 'var(--text-subtle)' }}>{t('settings.noSignature')}</span>}
              </div>
              <div>
                <input type="file" accept="image/*" onChange={handleSignature} style={{ fontSize: 13, color: 'var(--text)' }} />
                {form.signatureUrl && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, signatureUrl: '' }))}
                    style={{ display: 'block', marginTop: 8, fontSize: 12, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    {t('settings.removeSignature')}
                  </button>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 6 }}>{t('settings.pngRecommended')}</p>
              </div>
            </div>
          </div>
        </Section>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Btn type="submit" variant="primary" size="lg" disabled={saving}>
            {saving ? t('common.saving') : `💾 ${t('settings.saveAll')}`}
          </Btn>
        </div>
      </div>
    </form>
  );
}
